<template>
  <div class="google-maps-picker-container" ref="wrapperEl">
    <!-- Map Canvas Wrapper with Responsive Height -->
    <div class="map-canvas-wrapper">
      <div
        ref="mapContainer"
        class="map-canvas"
        :class="{ 'is-loading': gpsState === 'loading' }"
      ></div>

      <!-- Floating Controls on Top-Right (Native Google Maps Style) -->
      <div v-if="gpsState !== 'loading'" class="map-floating-controls">
        <button
          type="button"
          class="map-floating-btn"
          @click="requestGPS(true)"
          :title="$t('sendReport.mapMyLocation')"
          :aria-label="$t('sendReport.mapMyLocation')"
        >
          <svg class="gps-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#2563eb" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
          </svg>
        </button>
        <button
          type="button"
          class="map-floating-btn btn-mfu"
          @click="resetToMFU"
          :title="$t('sendReport.mapResetMFU')"
          :aria-label="$t('sendReport.mapResetMFU')"
        >
          <span class="mfu-icon">🏛️</span>
        </button>
      </div>

      <!-- Floating Alert if GPS Denied or Unsupported -->
      <transition name="fade">
        <div v-if="gpsState === 'denied' || gpsState === 'unsupported'" class="floating-alert-banner">
          <span class="alert-icon">⚠️</span>
          <span class="alert-text">{{ $t('sendReport.mapGpsDeniedAlert') }}</span>
          <button type="button" class="alert-close-btn" @click="gpsState = 'idle'">✕</button>
        </div>
      </transition>

      <!-- Spinner Overlay หมุนโหลดตรงกลาง -->
      <transition name="fade">
        <div v-if="gpsState === 'loading'" class="map-status-overlay">
          <div class="map-status-box">
            <div class="spinner-border text-primary mb-3" role="status" style="width: 2.5rem; height: 2.5rem;">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mb-1 font-weight-bold text-dark" style="font-size: 1rem;">{{ $t('sendReport.mapGpsLoading') }}</p>
            <small class="text-muted">{{ $t('sendReport.mapGpsPermissionHint') }}</small>
          </div>
        </div>
      </transition>

      <!-- Floating Coordinates Badge (Bottom-Left) -->
      <div v-if="gpsState !== 'loading'" class="floating-coords-badge">
        <span class="coords-icon">📍</span>
        <span class="coords-text">{{ currentMarker.lat.toFixed(6) }}, {{ currentMarker.lng.toFixed(6) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GoogleMapsPicker',
  props: {
    value: {
      type: Object,
      default: () => null
    }
  },
  data() {
    const MFU_LAT = 20.04502266214119;
    const MFU_LNG = 99.89483339570916;

    const initialLat = (this.value && this.value.lat) ? Number(this.value.lat) : MFU_LAT;
    const initialLng = (this.value && this.value.lng) ? Number(this.value.lng) : MFU_LNG;

    return {
      MFU_LAT,
      MFU_LNG,
      map: null,
      marker: null,
      currentMarker: { lat: initialLat, lng: initialLng },
      gpsState: 'loading', // 'loading' | 'success' | 'denied' | 'unsupported' | 'idle'
      mapReady: false,
      resizeObserver: null,
      resizeTimers: [],
      resizeDebounceTimer: null,
    };
  },
  mounted() {
    // 1. รอให้ container มีขนาดจริงก่อนสร้าง Map
    this.initWhenReady();
    // 2. ขอพิกัด GPS อัตโนมัติ
    this.requestGPS();
    // 3. ดักฟัง window resize และ orientation change เพื่อให้ Responsive เสมอ
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('orientationchange', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('orientationchange', this.onWindowResize);
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.clearResizeTimers();
    if (this.marker) {
      this.marker.setMap(null);
      this.marker = null;
    }
    this.map = null;
  },
  methods: {
    clearResizeTimers() {
      if (this.resizeTimers && this.resizeTimers.length) {
        this.resizeTimers.forEach(t => clearTimeout(t));
        this.resizeTimers = [];
      }
    },

    initWhenReady(attempt = 0) {
      const container = this.$refs.mapContainer;
      // ถ้า container ยังไม่มีขนาด (กำลังทำ transition หรือยังไม่ mount จริง) ให้รอรอบถัดไป
      if (!container || (container.clientHeight < 50 && attempt < 15)) {
        const timer = setTimeout(() => this.initWhenReady(attempt + 1), 50);
        this.resizeTimers.push(timer);
        return;
      }

      this.initMap();
    },

    waitForGoogle(callback, retries = 50) {
      if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
        callback();
      } else if (retries > 0) {
        const timer = setTimeout(() => this.waitForGoogle(callback, retries - 1), 100);
        this.resizeTimers.push(timer);
      }
    },

    initMap() {
      if (this.mapReady || this.map) return;

      this.waitForGoogle(() => {
        const container = this.$refs.mapContainer;
        if (!container || this.map) return;

        this.map = new google.maps.Map(container, {
          center: this.currentMarker,
          zoom: 17,
          mapTypeId: google.maps.MapTypeId.HYBRID,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: true,
          fullscreenControl: false,
          gestureHandling: 'greedy', // เลื่อนแผนที่บนมือถือได้ลื่นไหล
        });

        this.marker = new google.maps.Marker({
          position: this.currentMarker,
          map: this.map,
          draggable: true,
          title: this.$t('sendReport.mapMarkerTitle'),
          animation: google.maps.Animation.DROP,
        });

        this.marker.addListener('dragend', () => {
          const pos = this.marker.getPosition();
          this.updateLocation(pos.lat(), pos.lng());
        });

        this.map.addListener('click', (event) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          this.marker.setPosition({ lat, lng });
          this.marker.setAnimation(google.maps.Animation.BOUNCE);
          setTimeout(() => {
            if (this.marker) this.marker.setAnimation(null);
          }, 600);
          this.updateLocation(lat, lng);
        });

        this.mapReady = true;
        this.setupResizeObserver();
        this.scheduleResizes();
        this.emitLocation(this.currentMarker);
      });
    },

    setupResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      const container = this.$refs.mapContainer;
      if (!container) return;

      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect && entry.contentRect.height > 50) {
            requestAnimationFrame(() => {
              this.forceResize();
            });
          }
        }
      });
      this.resizeObserver.observe(container);
    },

    scheduleResizes() {
      // Trigger resize หลังจาก CSS transition ทำงานเสร็จสิ้นในแต่ละช่วง
      const delays = [50, 150, 320, 500, 800];
      delays.forEach(delay => {
        const timer = setTimeout(() => {
          this.forceResize();
        }, delay);
        this.resizeTimers.push(timer);
      });
    },

    onWindowResize() {
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }
      this.resizeDebounceTimer = setTimeout(() => {
        this.forceResize();
      }, 120);
    },

    forceResize() {
      if (!this.map) return;
      google.maps.event.trigger(this.map, 'resize');
      if (this.currentMarker) {
        this.map.setCenter(this.currentMarker);
      }
    },

    requestGPS(forceNotice = false) {
      if (!navigator.geolocation) {
        this.gpsState = 'unsupported';
        this.forceResize();
        return;
      }

      this.gpsState = 'loading';

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.currentMarker = { lat, lng };
          this.gpsState = 'success';

          if (this.map && this.marker) {
            this.marker.setPosition({ lat, lng });
            this.map.panTo({ lat, lng });
            this.map.setZoom(18);
            this.marker.setAnimation(google.maps.Animation.DROP);
          }

          this.emitLocation(this.currentMarker);
          this.forceResize();
          this.scheduleResizes();
        },
        (error) => {
          console.warn('[GPS Geolocation] Warning:', error.code, error.message);
          this.gpsState = 'denied';
          this.emitLocation(this.currentMarker);
          this.forceResize();
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },

    resetToMFU() {
      const lat = this.MFU_LAT;
      const lng = this.MFU_LNG;
      this.currentMarker = { lat, lng };
      if (this.map && this.marker) {
        this.marker.setPosition({ lat, lng });
        this.map.panTo({ lat, lng });
        this.map.setZoom(16);
        this.marker.setAnimation(google.maps.Animation.DROP);
      }
      this.emitLocation(this.currentMarker);
      this.forceResize();
    },

    updateLocation(lat, lng) {
      this.currentMarker = { lat, lng };
      this.emitLocation(this.currentMarker);
    },

    emitLocation(location) {
      this.$emit('input', { lat: location.lat, lng: location.lng });
    },
  },
};
</script>

<style scoped>
.google-maps-picker-container {
  width: 100%;
  display: block;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
  box-sizing: border-box;
}

/* Map Canvas Wrapper & Responsive Height */
.map-canvas-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #e5e3df; /* Standard maps bg color */
}

.map-canvas {
  width: 100%;
  height: 420px;
  min-height: 270px;
  position: relative;
  transition: filter 0.2s ease;
}

.map-canvas.is-loading {
  filter: blur(2px);
  pointer-events: none;
}

/* Force Google Maps container and internals to fill 100% of the canvas */
::v-deep .gm-style,
::v-deep .gm-style > div:first-child {
  height: 100% !important;
  width: 100% !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
}

/* Floating Controls (Top Right - Native Google Maps style) */
.map-floating-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-floating-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #ffffff;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.22);
  color: #374151;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.map-floating-btn:hover:not(:disabled) {
  background: #f8fafc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}

.map-floating-btn:active:not(:disabled) {
  transform: translateY(0);
}

.map-floating-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.gps-icon {
  transition: transform 0.15s ease;
}

.map-floating-btn:hover .gps-icon {
  transform: scale(1.1);
}

.mfu-icon {
  font-size: 1.1rem;
  line-height: 1;
}

/* Floating Alert Banner */
.floating-alert-banner {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  color: #92400e;
  border-radius: 8px;
  font-size: 0.8125rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 90%;
}

.alert-icon {
  flex-shrink: 0;
  font-size: 1rem;
}

.alert-text {
  flex: 1;
}

.alert-close-btn {
  background: transparent;
  border: none;
  color: #92400e;
  font-size: 0.875rem;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}

/* Spinner Overlay หมุนโหลดตรงกลาง */
.map-status-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.map-status-box {
  text-align: center;
  padding: 1.75rem 2rem;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  max-width: 290px;
  width: 85%;
  animation: popIn 0.2s ease-out;
}

@keyframes popIn {
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Floating Coordinates Badge (Bottom-Left) */
.floating-coords-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(17, 24, 39, 0.85);
  backdrop-filter: blur(4px);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  font-family: "Sarabun", sans-serif;
}

/* Responsive Media Queries */
@media (max-width: 991px) {
  .map-canvas {
    height: 360px;
  }
}

@media (max-width: 768px) {
  .map-canvas {
    height: 310px;
  }

  .floating-coords-badge {
    bottom: 8px;
    left: 8px;
    padding: 4px 8px;
    font-size: 0.7rem;
  }

  .map-floating-controls {
    top: 8px;
    right: 8px;
    gap: 6px;
  }

  .map-floating-btn {
    width: 36px;
    height: 36px;
  }
}

@media (max-width: 480px) {
  .map-canvas {
    height: 270px;
  }
}
</style>
